import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import type {
  NavigationMeta,
  NavigationContextValue,
} from "@/types/navigation.types";

const NavigationContext = createContext<NavigationContextValue | undefined>(
  undefined,
);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [meta, setMeta] = useState<NavigationMeta>({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const workflowExitHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const timelineExitHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const navExitHandlerRef = useRef<(() => Promise<void>) | null>(null);
  // Pre-navigation check handlers - return true to allow navigation, false to block
  const workflowPreNavCheckRef = useRef<
    ((targetPath: string) => Promise<boolean>) | null
  >(null);
  const timelinePreNavCheckRef = useRef<
    ((targetPath: string) => Promise<boolean>) | null
  >(null);

  const setNavigationMeta = useCallback((newMeta: Partial<NavigationMeta>) => {
    setMeta((prev) => ({ ...prev, ...newMeta }));
  }, []);

  const clearNavigationMeta = useCallback(() => {
    setMeta({});
  }, []);

  const setLoading = useCallback((loading: boolean, message = "Loading...") => {
    setIsLoading(loading);
    setLoadingMessage(message);
  }, []);

  const registerWorkflowExitHandler = useCallback(
    (handler: (() => Promise<void>) | null) => {
      workflowExitHandlerRef.current = handler;
    },
    [],
  );

  const triggerWorkflowExit = useCallback(async () => {
    if (workflowExitHandlerRef.current) {
      await workflowExitHandlerRef.current();
    }
  }, []);

  const registerWorkflowPreNavCheck = useCallback(
    (handler: ((targetPath: string) => Promise<boolean>) | null) => {
      workflowPreNavCheckRef.current = handler;
    },
    [],
  );

  // Returns true if navigation should proceed, false if blocked/cancelled
  const checkWorkflowNavigation = useCallback(
    async (targetPath: string): Promise<boolean> => {
      if (workflowPreNavCheckRef.current) {
        return await workflowPreNavCheckRef.current(targetPath);
      }
      return true; // No check registered, allow navigation
    },
    [],
  );

  const registerTimelineExitHandler = useCallback(
    (handler: (() => Promise<void>) | null) => {
      timelineExitHandlerRef.current = handler;
    },
    [],
  );

  const triggerTimelineExit = useCallback(async () => {
    if (timelineExitHandlerRef.current) {
      await timelineExitHandlerRef.current();
    }
  }, []);

  const registerTimelinePreNavCheck = useCallback(
    (handler: ((targetPath: string) => Promise<boolean>) | null) => {
      timelinePreNavCheckRef.current = handler;
    },
    [],
  );

  // Returns true if navigation should proceed, false if blocked/cancelled
  const checkTimelineNavigation = useCallback(
    async (targetPath: string): Promise<boolean> => {
      if (timelinePreNavCheckRef.current) {
        return await timelinePreNavCheckRef.current(targetPath);
      }
      return true; // No check registered, allow navigation
    },
    [],
  );

  const registerNavExitHandler = useCallback(
    (handler: (() => Promise<void>) | null) => {
      navExitHandlerRef.current = handler;
    },
    [],
  );

  const triggerNavExit = useCallback(async () => {
    if (navExitHandlerRef.current) {
      await navExitHandlerRef.current();
    }
  }, []);

  const value = useMemo(
    () => ({
      meta,
      isLoading,
      loadingMessage,
      setNavigationMeta,
      clearNavigationMeta,
      setLoading,
      registerWorkflowExitHandler,
      triggerWorkflowExit,
      registerWorkflowPreNavCheck,
      checkWorkflowNavigation,
      registerTimelineExitHandler,
      triggerTimelineExit,
      registerTimelinePreNavCheck,
      checkTimelineNavigation,
      registerNavExitHandler,
      triggerNavExit,
    }),
    [
      meta,
      isLoading,
      loadingMessage,
      setNavigationMeta,
      clearNavigationMeta,
      setLoading,
      registerWorkflowExitHandler,
      triggerWorkflowExit,
      registerWorkflowPreNavCheck,
      checkWorkflowNavigation,
      registerTimelineExitHandler,
      triggerTimelineExit,
      registerTimelinePreNavCheck,
      checkTimelineNavigation,
      registerNavExitHandler,
      triggerNavExit,
    ],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextValue => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};
